import React, { useMemo } from "react";
import type {
  JobcardManpowerJobLineItem,
  JobcardManpowerJobLineItems,
} from "../types/jobcardManpower";
import { jobcardManpowerFormatCurrency } from "../utils/jobcardManpowerUtils";

function formatNum(val: number | string | undefined): string {
  if (val === undefined || val === null || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return n % 1 === 0 ? String(n) : n.toFixed(2);
}

function formatMoney(val: number | string | undefined): string {
  if (val === undefined || val === null || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return jobcardManpowerFormatCurrency(n);
}

type SectionKey = keyof JobcardManpowerJobLineItems;

const SECTION_LABELS: Record<SectionKey, string> = {
  labour: "Labour / Others",
  spares: "Spare parts",
  miscellaneous: "Miscellaneous",
};

const LineItemsTable: React.FC<{
  section: SectionKey;
  rows: JobcardManpowerJobLineItem[];
}> = ({ section, rows }) => {
  const showNarration = section === "labour";

  if (rows.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2">
        No {SECTION_LABELS[section].toLowerCase()} lines on this job card.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-100/80 dark:bg-gray-800 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <th className="px-2 py-1.5 font-semibold w-8">#</th>
            <th className="px-2 py-1.5 font-semibold min-w-[120px]">Item / Code</th>
            {showNarration ? (
              <th className="px-2 py-1.5 font-semibold min-w-[100px]">Narration</th>
            ) : null}
            <th className="px-2 py-1.5 font-semibold text-right w-12">Qty</th>
            <th className="px-2 py-1.5 font-semibold text-right w-16">Rate</th>
            <th className="px-2 py-1.5 font-semibold text-right w-16">Amount</th>
            <th className="px-2 py-1.5 font-semibold text-right w-16">Net</th>
            <th className="px-2 py-1.5 font-semibold min-w-[64px]">Dept</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={String(row.id ?? idx)}
              className="border-t border-gray-100 dark:border-gray-700/80"
            >
              <td className="px-2 py-1.5 text-gray-500">{idx + 1}</td>
              <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 break-words">
                {row.description?.trim() || "—"}
              </td>
              {showNarration ? (
                <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 break-words">
                  {row.narration?.trim() || "—"}
                </td>
              ) : null}
              <td className="px-2 py-1.5 text-right tabular-nums">
                {formatNum(row.qty)}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">
                {formatMoney(row.rate)}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">
                {formatMoney(row.amount)}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                {formatMoney(row.net_amt)}
              </td>
              <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">
                {row.department?.trim() || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

type JobcardManpowerLineItemsPanelProps = {
  lineItems: JobcardManpowerJobLineItems;
  loading?: boolean;
};

export const JobcardManpowerLineItemsPanel: React.FC<
  JobcardManpowerLineItemsPanelProps
> = ({ lineItems, loading }) => {
  const totalCount = useMemo(
    () =>
      lineItems.labour.length +
      lineItems.spares.length +
      lineItems.miscellaneous.length,
    [lineItems]
  );

  const sections = useMemo(
    () =>
      (["spares", "labour", "miscellaneous"] as SectionKey[]).filter(
        (k) => lineItems[k].length > 0 || loading
      ),
    [lineItems, loading]
  );

  if (!loading && totalCount === 0) {
    return null;
  }

  return (
    <section
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
      aria-label="Job card line items"
    >
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Job card line items
        </p>
        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          {loading ? "Loading…" : `${totalCount} line${totalCount === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {(loading && totalCount === 0
          ? (["spares", "labour", "miscellaneous"] as SectionKey[])
          : sections
        ).map((key) => (
          <div key={key}>
            <div className="px-3 py-1.5 bg-gray-50/60 dark:bg-gray-800/40 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                {SECTION_LABELS[key]}
              </span>
              <span className="text-[10px] text-gray-500">
                {loading && lineItems[key].length === 0
                  ? "…"
                  : `${lineItems[key].length} item${lineItems[key].length === 1 ? "" : "s"}`}
              </span>
            </div>
            {loading && lineItems[key].length === 0 ? (
              <p className="text-xs text-gray-400 px-3 py-2">Loading lines…</p>
            ) : (
              <LineItemsTable section={key} rows={lineItems[key]} />
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
